import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const activities = [
  { icon: 'ri-pencil-ruler-2-line', label: 'Design graphique' },
  { icon: 'ri-eye-line', label: 'Conception visuelle' },
  { icon: 'ri-layout-grid-line', label: 'Création de supports' },
];

export default function About() {
  const heroRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const refs = [heroRef, contentRef];
    const observers = refs.map((ref) => {
      const el = ref.current;
      if (!el) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              el.querySelectorAll('.about-animate').forEach((item, i) => {
                setTimeout(() => {
                  item.classList.add('animate-fade-up');
                  item.classList.remove('opacity-0-init');
                }, i * 120);
              });
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="min-h-[60vh] flex items-end bg-[#FAFAF8] pt-28 pb-16 md:pb-24 px-6 md:px-10">
        <div className="max-w-5xl mx-auto w-full">
          <span className="about-animate opacity-0-init block text-xs font-medium tracking-[0.3em] text-stone-400 uppercase mb-6">
            À propos
          </span>
          <h1 className="about-animate opacity-0-init font-serif-display text-4xl md:text-6xl lg:text-7xl font-bold text-stone-900 leading-tight max-w-4xl">
            KURTA est une activité indépendante de{' '}
            <em className="not-italic font-light italic text-stone-500">design</em>{' '}
            basée à Bordeaux.
          </h1>
        </div>
      </section>

      {/* Content */}
      <section ref={contentRef} className="py-20 md:py-28 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20">
          {/* Left */}
          <div>
            <p className="about-animate opacity-0-init text-base md:text-lg text-stone-600 leading-[1.85] mb-8">
              Le studio est dirigé par{' '}
              <strong className="font-semibold text-stone-900">Dawid Kurta</strong>, designer proposant des solutions visuelles pour différents projets, aussi bien pour des entreprises que pour des particuliers à Bordeaux et au-delà.
            </p>
            <p className="about-animate opacity-0-init text-base md:text-lg text-stone-600 leading-[1.85]">
              L&apos;objectif est de proposer des solutions simples, efficaces et adaptées à chaque client, grâce à une approche de design graphique attentive et rigoureuse.
            </p>
          </div>

          {/* Right – activities */}
          <div className="about-animate opacity-0-init">
            <h2 className="text-xs font-semibold tracking-[0.3em] text-stone-400 uppercase mb-6">L&apos;activité comprend</h2>
            <ul className="flex flex-col gap-4">
              {activities.map((act) => (
                <li
                  key={act.label}
                  className="flex items-center gap-4 py-4 border-b border-stone-100"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100">
                    <i className={`${act.icon} text-lg text-stone-700`} />
                  </div>
                  <span className="text-base font-medium text-stone-800">{act.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Info card */}
        <div className="about-animate opacity-0-init bg-[#F4EBE0] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl font-bold text-stone-900 mb-1">KURTA</h3>
            <p className="text-sm text-stone-600">Entrepreneur individuel – Dawid Kurta</p>
            <p className="text-sm text-stone-500 mt-1">SIRET : 102 042 504 00013</p>
            <p className="text-sm text-stone-500 mt-1">74.10Z – Activités spécialisées de design</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-stone-700 transition-colors duration-200 cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            Nous contacter
            <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}